import { Box, Button, CircularProgress, Theme, useTheme } from "@mui/material";
import { useIntl } from "react-intl";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CourseModel } from "services/facultyPortalClient";
import ClaimCreditButton from "pages/CoursesPage/ClaimCreditButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const getStyles = (theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    px: 1.5,
    pb: 1.5,
    borderRadius: 1,
    backgroundColor: theme.palette.background.default,
    minWidth: "300px",
    [theme.breakpoints.down("lg")]: {
      mb: 2,
    },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 1,
    border: `1px solid ${theme.palette.primary.light}`,
    gap: 1,
    p: 1,
  },
  sectionTitle: {
    m: 0,
  },
});

type CourseDetailsCtasProps = {
  course: CourseModel;
};

export default function CourseDetailsCtas({ course }: CourseDetailsCtasProps) {
  const { formatMessage: t } = useIntl();
  const styles = getStyles(useTheme());
  const navigate = useNavigate();
  const { agreementReviewUrl, courseService } = useGlobalContext();
  const [isLoadingWebinar, setIsLoadingWebinar] = useState(false);

  const showViewAgreement = course.hasAgreement;
  const showJoinWebinar = course.isLive && course.hasLiveEventDeliveryMethod && course.hasAgreement;

  const handleViewAgreement = () => {
    navigate(agreementReviewUrl(course.id, course.skuUserAgreementId!));
  };

  const handleJoinWebinar = async () => {
    setIsLoadingWebinar(true);
    try {
      const response = await courseService.getWebinarUrl(course.id);
      window.open(response.url, "_blank");
    } finally {
      setIsLoadingWebinar(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <h3>{t({ id: "courseActions" })}</h3>

      {showViewAgreement && (
        <Box sx={styles.section}>
          <Box component="h5" sx={styles.sectionTitle}>
            {t({ id: "agreement" })}
          </Box>
          <Button fullWidth variant="contained" onClick={handleViewAgreement}>
            {t({ id: "viewAgreement" })}
          </Button>
        </Box>
      )}

      {showJoinWebinar && (
        <Box sx={styles.section}>
          <Box component="h5" sx={styles.sectionTitle}>
            {t({ id: "webinar" })}
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handleJoinWebinar}
            disabled={isLoadingWebinar}
            startIcon={
              isLoadingWebinar ? <CircularProgress size={16} /> : undefined
            }
            endIcon={
              !isLoadingWebinar ? <OpenInNewIcon fontSize="small" /> : undefined
            }
          >
            {t({ id: "joinWebinar" })}
          </Button>
        </Box>
      )}

      <Box sx={styles.section}>
        <Box component="h5" sx={styles.sectionTitle}>
          {t({ id: "claimCredit" })}
        </Box>
        <ClaimCreditButton course={course} alwaysShow />
      </Box>
    </Box>
  );
}
